import React, { useEffect, useState, useMemo } from "react";
import { getMedications } from "../services/medication";
import { CreateMedication } from "../components/modals/CreateMedication";
import Spinner from "../components/PageLoader";
import { Cards } from "../components/Cards.tsx";
import { useTheme } from "../context/ThemeContext.js";

export default function Medication() {
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [medications, setMedications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("table"); // table or cards
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const fetchMedication = async () => {
    setIsLoading(true);
    try {
      const { data } = await getMedications();
      setMedications(data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMedication();
  }, []);

  const filteredAndSortedMedications = useMemo(() => {
    let filtered = medications.filter(medication => {
      const matchesSearch = medication.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (medication.description && medication.description.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesSearch;
    });

    // Sorting logic
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [medications, searchTerm, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key === columnName) {
      return sortConfig.direction === 'asc' ? '↑' : '↓';
    }
    return '⥯';
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'success';
      case 'inactive': return 'danger';
      case 'pending': return 'warning';
      default: return 'primary';
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="medication-business__loader">
          <Spinner />
        </div>
      ) : (
        <div className="medication-business" data-theme={theme}>
          {/* Business Header with Analytics */}
          <Cards classes="mb-24 medication-business__header">
            <div className="medication-business__header-content">
              <div className="medication-business__title-section">
                <div className="medication-business__icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M17.5 12C17.5 12.83 16.83 13.5 16 13.5H13.5V16C13.5 16.83 12.83 17.5 12 17.5S10.5 16.83 10.5 16V13.5H8C7.17 13.5 6.5 12.83 6.5 12S7.17 10.5 8 10.5H10.5V8C10.5 7.17 11.17 6.5 12 6.5S13.5 7.17 13.5 8V10.5H16C16.83 10.5 17.5 11.17 17.5 12ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z" fill="white"/>
                  </svg>
                </div>
                <div>
                  <h4 className="medication-business__title">Medication Database</h4>
                  <p className="medication-business__subtitle">
                    Comprehensive medication management and inventory system
                  </p>
                </div>
              </div>
              <div className="medication-business__stats">
                <div className="medication-business__stat">
                  <span className="medication-business__stat-value medication-business__status--success">{medications.filter(m => m.status !== 'inactive').length}</span>
                  <span className="medication-business__stat-label">Active</span>
                </div>
              </div>
            </div>
          </Cards>

          {/* Business Controls and Filters */}
          <Cards classes="mb-24 medication-business__controls">
            <div className="medication-business__controls-header">
              <div className="medication-business__controls-left">
                <div className="medication-business__search">
                  <div className="medication-business__search-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="M21 21l-4.35-4.35"/>
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search medications by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="medication-business__search-input"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="medication-business__search-clear"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  )}
                </div>

              </div>

              <div className="medication-business__controls-right">
                <div className="medication-business__view-toggle">
                  <button
                    className={`medication-business__view-btn ${viewMode === 'table' ? 'medication-business__view-btn--active' : ''}`}
                    onClick={() => setViewMode('table')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h4"/>
                    </svg>
                    Table
                  </button>
                  <button
                    className={`medication-business__view-btn ${viewMode === 'cards' ? 'medication-business__view-btn--active' : ''}`}
                    onClick={() => setViewMode('cards')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7"/>
                      <rect x="14" y="3" width="7" height="7"/>
                      <rect x="3" y="14" width="7" height="7"/>
                      <rect x="14" y="14" width="7" height="7"/>
                    </svg>
                    Cards
                  </button>
                </div>

                <button
                  className="medication-business__action-btn medication-business__action-btn--primary"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add Medication
                </button>
              </div>
            </div>
          </Cards>

          {/* Data Display */}
          <Cards classes="medication-business__data">
            {filteredAndSortedMedications.length === 0 ? (
              <div className="medication-business__empty">
                <div className="medication-business__empty-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14,2 14,8 20,8"/>
                    <line x1="12" y1="11" x2="12" y2="17"/>
                    <line x1="9" y1="14" x2="15" y2="14"/>
                  </svg>
                </div>
                <h5 className="medication-business__empty-title">
                  {searchTerm ? 'No medications match your criteria' : 'No medications found'}
                </h5>
                <p className="medication-business__empty-description">
                  {searchTerm
                    ? 'Try adjusting your search terms to find what you\'re looking for.'
                    : 'Get started by adding your first medication to the database.'
                  }
                </p>
                {!searchTerm && (
                  <button
                    className="medication-business__action-btn medication-business__action-btn--primary"
                    onClick={() => setIsCreateModalOpen(true)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Add First Medication
                  </button>
                )}
              </div>
            ) : viewMode === 'table' ? (
              <div className="medication-business__table-container">
                <table className="medication-business__table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')} className="medication-business__th medication-business__th--sortable">
                        Medication Name {getSortIcon('name')}
                      </th>
                      <th onClick={() => handleSort('status')} className="medication-business__th medication-business__th--sortable">
                        Status {getSortIcon('status')}
                      </th>
                      <th className="medication-business__th">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedMedications.map((medication, index) => (
                      <tr key={medication.id || medication.name + index} className="medication-business__tr">
                        <td className="medication-business__td">
                          <div className="medication-business__medication-cell">
                            <div className="medication-business__medication-icon">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="10" rx="2" ry="2"/>
                                <circle cx="12" cy="5" r="2"/>
                                <path d="M12 7v4"/>
                                <line x1="8" y1="16" x2="16" y2="16"/>
                                <line x1="10" y1="20" x2="14" y2="20"/>
                              </svg>
                            </div>
                            <div>
                              <div className="medication-business__medication-name">{medication.name}</div>
                              {medication.description && (
                                <div className="medication-business__medication-description">{medication.description}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="medication-business__td">
                          <span className={`medication-business__status medication-business__status--${getStatusColor(medication.status || 'active')}`}>
                            {medication.status || 'Active'}
                          </span>
                        </td>
                        <td className="medication-business__td">
                          <div className="medication-business__actions">
                            <button className="medication-business__action-btn-small" title="Edit">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button className="medication-business__action-btn-small" title="Delete">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3,6 5,6 21,6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                              </svg>
                            </button>
                            <button className="medication-business__action-btn-small" title="More options">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="1"/>
                                <circle cx="19" cy="12" r="1"/>
                                <circle cx="5" cy="12" r="1"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="medication-business__cards-grid">
                {filteredAndSortedMedications.map((medication, index) => (
                  <div className="medication-business__card" key={medication.id || medication.name + index}>
                    <div className="medication-business__card-header">
                      <div className="medication-business__card-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="10" rx="2" ry="2"/>
                          <circle cx="12" cy="5" r="2"/>
                          <path d="M12 7v4"/>
                          <line x1="8" y1="16" x2="16" y2="16"/>
                          <line x1="10" y1="20" x2="14" y2="20"/>
                        </svg>
                      </div>
                      <span className={`medication-business__card-status medication-business__card-status--${getStatusColor(medication.status || 'active')}`}>
                        {medication.status || 'Active'}
                      </span>
                    </div>
                    <div className="medication-business__card-content">
                      <h6 className="medication-business__card-title">{medication.name}</h6>
                      {medication.description && (
                        <p className="medication-business__card-description">{medication.description}</p>
                      )}
                      <div className="medication-business__card-details">
                        <div className="medication-business__card-detail">
                          <span className="medication-business__card-detail-label">Dosage:</span>
                          <span className="medication-business__card-detail-value">{medication.dosage || 'Not specified'}</span>
                        </div>
                        <div className="medication-business__card-detail">
                          <span className="medication-business__card-detail-label">Frequency:</span>
                          <span className="medication-business__card-detail-value">{medication.frequency || 'As needed'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="medication-business__card-actions">
                      <button className="medication-business__card-action-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 20h9"/>
                          <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
                        </svg>
                        Edit
                      </button>
                      <button className="medication-business__card-action-btn medication-business__card-action-btn--danger">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          <line x1="10" y1="11" x2="10" y2="17"/>
                          <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Cards>
        </div>
      )}

      {isCreateModalOpen && (
        <CreateMedication
          isOpen={isCreateModalOpen}
          onClose={setIsCreateModalOpen}
          reload={fetchMedication}
        />
      )}
    </>
  );
}
